import { addDoc, collection, doc, getDoc, getDocs, query, setDoc, updateDoc, where } from "firebase/firestore"
import { db, storage } from "./config"
import { getSellerIdsForUser } from "./shopify"
import { getDocsWhereIn } from "./queries"
import { getDownloadURL, ref, uploadBytes } from "firebase/storage";

const generateUsername = (email) => {
    const username = email.split('@')[0];
    return username;
};

const getUserCountByUsernamePrefix = async (prefix) => {
    const usersRef = collection(db, 'users');

    // Create a query to filter usernames that start with the specified prefix
    const q = query(
        usersRef,
        where('username', '>=', prefix),
        where('username', '<', prefix + '\uf8ff') // This ensures it captures all usernames starting with the prefix
    );

    try {
        const querySnapshot = await getDocs(q);
        const userCount = querySnapshot.size; // Get the number of documents
        console.log(`Number of users with usernames starting with '${prefix}':`, userCount);
        return userCount;
    } catch (error) {
        console.error("Error getting users:", error);
        return 0; // Return 0 or handle error as needed
    }
};

export async function getFreeUsers(type) {
    try {
        let usersRef = collection(db, 'users')
        let q = query(usersRef, where('membership', '==', 'free'));

        const now = new Date();
        let startDate;

        // Determine the date range based on the type
        if (type === 'month') {
            startDate = new Date(now.setMonth(now.getMonth() - 1));
        } else if (type === 'week') {
            startDate = new Date(now.setDate(now.getDate() - 7));
        }

        // If type is not 'all', add a date filter
        if (type !== 'all') {
            q = query(usersRef,
                where('created_at', '<=', startDate),
                where('membership', '==', 'free')
            );
        }

        // Execute the query
        const snapshot = await getDocs(q);
        return snapshot.size; // Return the user count
    } catch (e) {
        console.error("Error fetching free users: ", e);
        throw e; // Rethrow the error for handling elsewhere if needed
    }
}

export async function getSubscriptionUsers(type) {
    try {
        let usersRef = collection(db, 'users')
        let q = query(usersRef, where('membership', '!=', 'free'));

        const now = new Date();
        let startDate;

        // Determine the date range based on the type
        if (type === 'month') {
            startDate = new Date(now.setMonth(now.getMonth() - 1));
        } else if (type === 'week') {
            startDate = new Date(now.setDate(now.getDate() - 7));
        }

        // If type is not 'all', add a date filter
        if (type !== 'all') {
            q = query(usersRef,
                where('created_at', '<=', startDate),
                where('membership', '==', 'free')
            );
        }

        // Execute the query
        const snapshot = await getDocs(q);
        return snapshot.size; // Return the user count
    } catch (e) {
        console.error("Error fetching free users: ", e);
        throw e; // Rethrow the error for handling elsewhere if needed
    }
}

export async function getTotalIncome(type) {
    try {
        const incomesRef = collection(db, 'incomes');
        let q = query(incomesRef); // Start with a basic query

        const now = new Date();
        let startDate;

        // Determine the date range based on the type
        if (type === 'month') {
            startDate = new Date(now.setMonth(now.getMonth() - 1));
        } else if (type === 'week') {
            startDate = new Date(now.setDate(now.getDate() - 7));
        }

        // If type is not 'all', add a date filter
        if (type !== 'all') {
            q = query(incomesRef, where('created_at', '>=', startDate));
        }

        // Execute the query
        const snapshot = await getDocs(q);

        // Calculate the total income
        let totalIncome = 0;
        snapshot.forEach(doc => {
            totalIncome += doc.data().amount; // Assuming 'amount' is the field for income
        });

        return totalIncome; // Return the total income
    } catch (e) {
        console.error("Error fetching total income: ", e);
        throw e; // Rethrow the error for handling elsewhere if needed
    }
}

export async function createUserProfile(userId, email) {
    try {
        const existing = await checkUserExists(userId)

        if (!existing) {
            const username = generateUsername(email)
            await setDoc(doc(db, "users", userId), {
                username: username,
                role: 'customer',
                gender: 'man',
                displayName: username,
                picture: "https://firebasestorage.googleapis.com/v0/b/card-market-place-a2684.firebasestorage.app/o/images%2Fusers%2Fdefault.png?alt=media&token=3da906c9-a825-4f0b-b47c-ab6d10eefe89",
                balance: 0,
                cartList: [],
                membership: 'free',
                monthly_sales: 0,
                email: email,
                created_at: new Date(),
                ratings: 0
            });
        }

        console.log("User profile created successfully");
    } catch (error) {
        console.error("Error creating user profile: ", error);
    }
}

export const updateUserProfile = async (userId, updatedData) => {
    try {
        // Reference to the specific user document
        let pictureURL

        const usersRef = doc(db, "users", userId)
        const docSnap = await getDoc(usersRef)
        const querySnapshot = docSnap.data()
        if (updatedData.picture !== null && updatedData.picture !== undefined) {
            const storageRef = ref(storage, `images/users/${querySnapshot.username}`)
            await uploadBytes(storageRef, updatedData.picture)
            pictureURL = await getDownloadURL(storageRef)
        }

        // Add a new document with a generated ID
        const newPictureURL = updatedData.picture ? pictureURL : querySnapshot.picture
        const newData = {
            ...updatedData,
            ...{
                picture: newPictureURL
            }
        }

        // Update the usersRef with new data
        await updateDoc(usersRef, newData)

        console.log("Document updated with ID: ", userId)
    } catch (e) {
        console.error("Error updating document: ", e)
    }
}

export const searchUsers = async (searchText) => {
    try {
        const usersRef = collection(db, "users");
        const querySnapshot = await getDocs(usersRef);
        const results = [];
        const lowerSearchText = searchText.toLowerCase(); // Convert search text to lowercase

        querySnapshot.forEach((doc) => {
            const data = doc.data();
            // Check if displayName or email includes the search text (case insensitive)
            if (
                (data.displayName && data.displayName.toLowerCase().includes(lowerSearchText)) ||
                (data.email && data.email.toLowerCase().includes(lowerSearchText))
            ) {
                results.push(data); // Include if either condition matches
            }
        });

        return results; // Return an array of matching users
    } catch (error) {
        console.error("Error searching users: ", error);
        return [];
    }
}

// export const checkUserExists = async (username) => {
//     try {
//         const usersRef = collection(db, "users")
//         const q = query(usersRef, where("username", "==", username))
//         const querySnapshot = await getDocs(q)

//         if (!querySnapshot.empty) {
//             return true // User exists
//         } else {
//             return false // User does not exist
//         }
//     } catch (e) {
//         console.error("Error checking user:", e)
//     }
// }

export const checkUserExists = async (userId) => {
    try {
        const userRef = doc(db, "users", userId);
        const userSnapshot = await getDoc(userRef);

        if (userSnapshot.exists()) {
            return true; // User exists
        } else {
            console.log("User does not exist");
            return false; // User does not exist
        }
    } catch (e) {
        console.error("Error checking user:", e)
    }
}

export const getUserData = async (userId) => {
    try {
        // Reference to the specific user document
        const usersRef = doc(db, "users", userId)
        const userSnapshot = await getDoc(usersRef)
        const userData = userSnapshot.data()

        const messagesRef = collection(db, "messages")
        const unReadMessages = []
        const q = query(messagesRef,
            where("recipientId", "==", userId),
            where("state", "==", 'unread'))
        const querySnapshot = await getDocs(q)

        const purchases = userData?.purchases ? userData?.purchases : 0

        querySnapshot.forEach((doc) => {
            unReadMessages.push({ id: doc.id, ...doc.data() })
        })

        const ratings = await getUserRating(userId)
        // const cartsRef = collection(db, "carts")
        // const carts = []
        // const q1 = query(cartsRef,
        //     where("user", "==", userId))
        // const querySnapshot1 = await getDocs(q1)

        // querySnapshot1.forEach((doc) => {
        //     carts.push({ id: doc.id, rating, ...doc.data() })
        // })

        return { ...userData, id: userId, ratings, unReadMessages: unReadMessages, purchases }
    } catch (e) {
        console.error("Error getting document: ", e)
    }
}

export const getUserRating = async (userId) => {
    try {
        // Reference to the "sales" collection
        const salesRef = collection(db, "sales")

        // Create a query to filter sales by sellerId
        // Includes sales through any Shopify store this account owns -- those
        // carry the store's role id as receiver, not the owner's uid.
        const querySnapshot = await getDocsWhereIn(salesRef, "receiver", await getSellerIdsForUser(userId))

        console.log("querynap", querySnapshot)

        if (querySnapshot.length === 0) return 0


        let ratings = 0, count = 0

        querySnapshot.forEach((doc) => {
            if (doc.data().review) {
                ratings += doc.data().review.rating
                count++
            }
        })

        if (ratings > 0)
            return parseFloat(ratings / count) // Return the array of sales

        else return 0
    } catch (e) {
        console.error("Error getting documents: ", e)
        return [] // Return an empty array in case of error
    }
}

export const getUserReviews = async (userId) => {
    try {
        // Reference to the "sales" collection
        const salesRef = collection(db, "sales")

        // Create a query to filter sales by sellerId
        // Includes sales through any Shopify store this account owns -- those
        // carry the store's role id as receiver, not the owner's uid.
        const querySnapshot = await getDocsWhereIn(salesRef, "receiver", await getSellerIdsForUser(userId))
        const reviews = []
        let countsByRating = [0, 0, 0, 0, 0]

        querySnapshot.forEach((doc) => {
            if (doc.data().review) {
                reviews.push(doc.data().review)
                countsByRating[doc.data().review.rating - 1]++
            }
        })

        return { reviews, countsByRating } // Return the array of sales
    } catch (e) {
        console.error("Error getting documents: ", e)
        return [] // Return an empty array in case of error
    }
}

export const getAllUserData = async () => {
    try {
        // Reference to the users collection
        const usersRef = collection(db, "users");
        const userSnapshot = await getDocs(usersRef);

        // Map through the documents and extract data
        const usersList = userSnapshot.docs.map(doc => ({
            id: doc.id,
            tickets: 0,
            ...doc.data()
        }));

        return usersList;
    } catch (e) {
        console.error("Error getting documents: ", e);
    }
}

function daysPassed(firstDate) {
    const startDate = firstDate;
    const currentDate = new Date(); // Gets the current date

    // Calculate the difference in milliseconds
    const diffInMilliseconds = currentDate - startDate;

    // Convert milliseconds to days
    const millisecondsInADay = 1000 * 60 * 60 * 24;
    const daysPassed = Math.ceil(diffInMilliseconds / millisecondsInADay);

    return daysPassed;
}

export const getDailyUsers = async () => {
    try {
        // Reference to the users collection
        const visitsRef = collection(db, "visits");
        const userSnapshot = await getDocs(visitsRef);
        // userSnapshot.size /

        let firstDate = new Date()

        userSnapshot.docs.map(doc => {
            if (firstDate > new Date(doc.data().created_at.seconds * 1000)) firstDate = new Date(doc.data().created_at.seconds * 1000)
        })

        const days = daysPassed(firstDate)

        return Math.ceil(userSnapshot.size / days)
    } catch (e) {
        console.error("Error getting documents: ", e);
    }
}

export const getAllUsersBySearchText = async (search) => {
    try {
        // Reference to the users collection
        const usersRef = collection(db, "users");
        const userSnapshot = await getDocs(usersRef);

        // Map through the documents and extract data
        const usersList = userSnapshot.docs.map(doc => ({
            id: doc.id,
            tickets: 0,
            ...doc.data()
        }));

        return usersList;
    } catch (e) {
        console.error("Error getting documents: ", e);
    }
}

export const addRecipient = async (userId, recipientId) => {
    try {
        // Reference to the specific user document
        const usersRef = doc(db, "users", userId)
        const userSnapshot = await getDoc(usersRef)
        const userData = userSnapshot.data()
        let recipients = userData.recipients ? userData.recipients : []
        const getRecipientData = await getUserData(recipientId)

        if (recipients.findIndex(item => item.id === recipientId) === -1) {
            recipients.push({ id: recipientId, displayName: getRecipientData.displayName, picture: getRecipientData.picture })
        }

        await updateDoc(usersRef, { ...userData, recipients: recipients })
    } catch (e) {
        console.error("Error getting document: ", e)
    }
}

export const getChartData = async (type) => {
    try {
        const data = []

        const visitsRef = collection(db, 'visits')

        if (type === 'daily') {
            for (let index = 0; index < 7; index++) {
                let now = new Date();
                let startDate = new Date(now.setDate(now.getDate() - (7 - index)));
                now = new Date();

                let endDate = new Date(now.setDate(now.getDate() - (6 - index)));

                let q = query(visitsRef,
                    where('created_at', '>=', startDate),
                    where('created_at', '<', endDate)
                );

                const snapshot = await getDocs(q);
                data.push(snapshot.size);
            }
        } else if (type === 'weekly') {
            for (let index = 0; index < 4; index++) {
                let now = new Date();
                let startDate = new Date(now.setDate(now.getDate() - (28 - (index * 7))));
                now = new Date();

                let endDate = new Date(now.setDate(now.getDate() - (21 - (index * 7))));

                let q = query(visitsRef,
                    where('created_at', '>=', startDate),
                    where('created_at', '<', endDate)
                );

                const snapshot = await getDocs(q);
                data.push(snapshot.size);
            }
        } else if (type === 'monthly') {
            for (let index = 0; index < 12; index++) {
                let now = new Date();
                let startDate = new Date(now.setFullYear(now.getFullYear(), now.getMonth() - (11 - index), 1));
                let endDate = new Date(now.setFullYear(now.getFullYear(), now.getMonth() - (10 - index) + 1, 0));

                let q = query(visitsRef,
                    where('created_at', '>=', startDate),
                    where('created_at', '<=', endDate)
                );

                const snapshot = await getDocs(q);
                data.push(snapshot.size);
            }
        }

        return data
    } catch (e) {

    }
}